import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Box, Button, Chip, Container, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import React, { useEffect } from 'react';

const HELPIGO_BLUE = '#2563eb';
const HELPIGO_SKY = '#99bbe3';

const WA_DEMO =
    'https://wa.me/905315404669?text=' +
    encodeURIComponent('Merhaba, Helpigo için demo talep ediyorum.');

export default function HomeLandingHero() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        if (!document.querySelector('script[src="https://tally.so/widgets/embed.js"]')) {
            const script = document.createElement('script');
            script.src = 'https://tally.so/widgets/embed.js';
            script.async = true;
            document.body.appendChild(script);
        }
    }, []);

    return (
        <Box
            component="section"
            sx={{
                position: 'relative',
                overflow: 'hidden',
                pt: { xs: 5, md: 8 },
                pb: { xs: 8, md: 12 },
                background: `
                  radial-gradient(ellipse 80% 60% at 50% -20%, rgba(37, 99, 235, 0.14), transparent 55%),
                  radial-gradient(ellipse 50% 40% at 100% 40%, ${HELPIGO_SKY}33, transparent 60%),
                  linear-gradient(180deg, #ffffff 0%, #f8fbff 45%, #f0f7ff 100%)
                `,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `
                      linear-gradient(rgba(37, 99, 235, 0.04) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(37, 99, 235, 0.04) 1px, transparent 1px)
                    `,
                    backgroundSize: '48px 48px',
                    maskImage: 'linear-gradient(180deg, black 0%, transparent 85%)',
                    WebkitMaskImage: 'linear-gradient(180deg, black 0%, transparent 85%)',
                    pointerEvents: 'none',
                },
            }}
        >
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center" direction={isMobile ? 'column-reverse' : 'row'}>
                    <Grid item xs={12} md={6}>
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <Chip
                                label="Yapay zeka · Pazaryeri · WhatsApp"
                                sx={{
                                    mb: 3,
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    height: 32,
                                    bgcolor: 'rgba(255,255,255,0.85)',
                                    border: `1px solid rgba(37, 99, 235, 0.2)`,
                                    color: HELPIGO_BLUE,
                                    backdropFilter: 'blur(8px)',
                                }}
                            />
                            <Typography
                                variant="h1"
                                component="h1"
                                sx={{
                                    fontSize: { xs: '2.35rem', sm: '3rem', md: '3.35rem', lg: '3.65rem' },
                                    fontWeight: 800,
                                    lineHeight: 1.08,
                                    letterSpacing: '-0.035em',
                                    color: 'text.primary',
                                    mb: 2.5,
                                }}
                            >
                                Müşteri sorularını{' '}
                                <Box component="span" sx={{ color: HELPIGO_BLUE }}>
                                    otomatik
                                </Box>
                                , markanıza uygun şekilde yanıtlayın
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: '1.05rem', md: '1.2rem' },
                                    color: 'text.secondary',
                                    lineHeight: 1.65,
                                    maxWidth: 520,
                                    mb: 1,
                                    fontWeight: 400,
                                }}
                            >
                                Helpigo; pazaryeri ve WhatsApp kanallarında soruları saniyeler içinde işler, onay akışınızla güvenle yayınlar.
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary', mb: 4, maxWidth: 480, opacity: 0.92 }}
                            >
                                Operasyon yükünü azaltın, ilk yanıt süresini düşürün, ekibinizi yalnızca kritik konuşmalara ayırın.
                            </Typography>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: 2,
                                    alignItems: { xs: 'stretch', sm: 'center' },
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Button
                                    variant="contained"
                                    size="large"
                                    endIcon={<ArrowForwardIcon />}
                                    data-tally-open="wMV9kA"
                                    data-tally-layout="modal"
                                    data-tally-width="400"
                                    data-tally-auto-close="0"
                                    sx={{
                                        py: 1.75,
                                        px: 3.5,
                                        borderRadius: 2,
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        bgcolor: HELPIGO_BLUE,
                                        boxShadow: '0 8px 24px rgba(37, 99, 235, 0.28)',
                                        '&:hover': {
                                            bgcolor: '#1d4ed8',
                                            boxShadow: '0 12px 28px rgba(37, 99, 235, 0.35)',
                                            transform: 'translateY(-1px)',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    Ücretsiz demo talep et
                                </Button>
                                <Button
                                    component="a"
                                    href={WA_DEMO}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="outlined"
                                    size="large"
                                    startIcon={<WhatsAppIcon />}
                                    sx={{
                                        py: 1.75,
                                        px: 3,
                                        borderRadius: 2,
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderColor: 'rgba(37, 99, 235, 0.35)',
                                        color: HELPIGO_BLUE,
                                        bgcolor: 'rgba(255,255,255,0.7)',
                                        '&:hover': {
                                            borderColor: HELPIGO_BLUE,
                                            bgcolor: 'rgba(255,255,255,0.95)',
                                        },
                                    }}
                                >
                                    WhatsApp ile yazın
                                </Button>
                                <Button
                                    component="a"
                                    href="https://www.youtube.com/watch?v=FywWrhGtON4&t=277s"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="text"
                                    size="large"
                                    startIcon={<PlayArrowIcon />}
                                    sx={{
                                        py: 1.75,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        color: 'text.primary',
                                        '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.06)' },
                                    }}
                                >
                                    Nasıl çalışır?
                                </Button>
                            </Box>
                        </motion.div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.96, y: 16 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <Box sx={{ position: 'relative', mx: 'auto', maxWidth: 560 }}>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: '-12%',
                                        borderRadius: '24px',
                                        background: `linear-gradient(135deg, ${HELPIGO_BLUE}22, ${HELPIGO_SKY}44)`,
                                        filter: 'blur(40px)',
                                        zIndex: 0,
                                    }}
                                />
                                <Box
                                    component="img"
                                    src="https://i.hizliresim.com/2xwjhyw.png"
                                    alt="Helpigo kontrol paneli önizlemesi"
                                    sx={{
                                        position: 'relative',
                                        zIndex: 1,
                                        width: '100%',
                                        height: 'auto',
                                        borderRadius: '20px',
                                        border: '1px solid rgba(37, 99, 235, 0.12)',
                                        boxShadow: `
                                          0 24px 48px rgba(15, 23, 42, 0.12),
                                          0 0 0 1px rgba(255,255,255,0.8) inset
                                        `,
                                    }}
                                />
                            </Box>
                        </motion.div>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}
